import React from 'react';
import { OverlayTrigger as BsOverlayTrigger } from 'react-bootstrap';

/**
 * The app's OverlayTrigger. Identical to react-bootstrap's, except that the
 * overlay is positioned with `position: fixed` instead of `position: absolute`.
 *
 * Why it exists: react-bootstrap portals every overlay into <body> and Popper
 * places it with the default `absolute` strategy. The staff SPA scrolls INSIDE
 * `.content-container` (`height: 100vh; overflow-y: auto`, Dashboard.css), so
 * the document itself is exactly one viewport tall and normally carries no
 * window scrollbar. An absolutely positioned box IS part of the document's
 * scrollable overflow, so any tooltip reaching past the fold — which is most
 * of them, on a table row below the middle of the screen — grew
 * `documentElement.scrollHeight` beyond `clientHeight`. The window scrollbar
 * then appeared for exactly as long as the pointer hovered, the viewport
 * narrowed by its width, and the whole page jumped sideways on every hover.
 * Measured in Chromium on the real shell markup: one tooltip took a 800px
 * document to 810px of scroll height.
 *
 * `strategy: 'fixed'` positions the overlay against the viewport instead, and
 * a fixed-position box contributes nothing to the document's scroll area — the
 * same measurement stays at 800px. Nothing else about the overlay changes:
 * Popper still tracks the trigger through ancestor scrolling, and the tooltip
 * still portals to <body>, whose only ancestors are <html> and <body> (a
 * transformed ancestor would re-anchor a fixed box, which is why this is safe
 * here and would not be inside `.content-container` — see the `page-in`
 * animation note in Dashboard.css).
 *
 * Import this everywhere instead of react-bootstrap's OverlayTrigger;
 * `__tests__/overlayTriggerStrategy.test.js` pins that. `TableExportButton`
 * applies the same `strategy: 'fixed'` to its Dropdown.Menu.
 */

const FIXED_STRATEGY = { strategy: 'fixed' };

const OverlayTrigger = ({ popperConfig, ...props }) => (
    <BsOverlayTrigger
        {...props}
        // A caller-supplied strategy still wins; nothing passes one today.
        popperConfig={popperConfig ? { ...FIXED_STRATEGY, ...popperConfig } : FIXED_STRATEGY}
    />
);

export default OverlayTrigger;
